import { useState, useEffect, useMemo, useCallback } from 'react';
import NavigationV2 from '../../../components/v2/NavigationV2.jsx';
import Toast from '../../../components/v2/Toast.jsx';
import useToast from '../../../hooks/useToast.js';
import {
    getSession,
    getCampusId,
    getUserId,
    getSessionId,
    getCampusList,
    getCampusGuid,
    getUsername,
    updateSessionCampus,
    getCampusConfig,
} from '../../../services/session.js';
import { getUTCTimestamp, getTTL } from '../../../libs.jsx';
import {
    fetchCampusConfig,
    fetchDeviceNames,
    fetchReasonCodes,
    fetchMysqlReasonCodes,
    fetchGlobalReasonCodes,
    fetchServerConfig,
    saveCampusConfig,
    editReasonCodes,
    addReasonCodes,
    deleteReasonCode,
    saveServerSettings,
} from './api/systemSettingsApi.js';

import AppSettingsTab from './components/AppSettingsTab.jsx';
import CustomizationTab from './components/CustomizationTab.jsx';
import AlertSettingsTab from './components/AlertSettingsTab.jsx';
import DeviceSettingsTab from './components/DeviceSettingsTab.jsx';
import ReasonSettingsTab from './components/ReasonSettingsTab.jsx';
import ServerSettingsTab from './components/ServerSettingsTab.jsx';
import DeviceDrawer from './components/DeviceDrawer.jsx';
import ReasonDrawer from './components/ReasonDrawer.jsx';
import DeleteReasonDialog from './components/DeleteReasonDialog.jsx';

import './system-settings-page.css';

// ---------------------------------------------------------------------------
// Tab definitions
// ---------------------------------------------------------------------------
const TABS = [
    { key: 'app', label: 'App' },
    { key: 'custom', label: 'Customization' },
    { key: 'alert', label: 'Alerts' },
    { key: 'device', label: 'Devices' },
    { key: 'reason', label: 'Reasons' },
    { key: 'server', label: 'Server' },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const SystemSettingsPage = () => {
    // -- Session --
    const [campusId, setCampusId] = useState(() => getCampusId());
    // Show all campuses (alphabetical, via getCampusList) so the user can switch
    // to a cloud campus; handleCampusChange reloads on a cloud↔non-cloud switch
    // and the /settings/system dispatcher renders the cloud page.
    const campusList = useMemo(() => getCampusList() || [], []);
    const campusName = useMemo(() => {
        const match = campusList.find((c) => c.campus_id === campusId);
        return match?.campus_name || `Campus ${campusId}`;
    }, [campusId, campusList]);

    // -- Tab state --
    const [activeTab, setActiveTab] = useState('app');

    // -- Data --
    const [configData, setConfigData] = useState({});
    const [deviceData, setDeviceData] = useState([]);
    const [reasonData, setReasonData] = useState([]);
    const [allReasonData, setAllReasonData] = useState([]);
    /* This campus's live core_reason_codes rows: what reports label with. Only used to gate
       "Create Defaults" (docs/REASON_CODE_FIX_PLAN.md B2). */
    const [mysqlReasonCount, setMysqlReasonCount] = useState(0);
    const [globalReasons, setGlobalReasons] = useState([]);
    const [serverData, setServerData] = useState({});

    // -- Loading --
    const [loading, setLoading] = useState(true);

    // -- Saving --
    const [saving, setSaving] = useState(false);

    // -- Drawers / Dialogs --
    const [deviceDrawerOpen, setDeviceDrawerOpen] = useState(false);
    const [reasonDrawerOpen, setReasonDrawerOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState({ id: '', description: '' });
    const [bulkDialogOpen, setBulkDialogOpen] = useState(false);

    // -- Toast --
    const { toast, showToast: showTypedToast, dismissToast } = useToast();
    /* This page's calls are (message, type); the shared hook is (type, message). */
    const showToast = useCallback((message, type = 'success') => showTypedToast(type, message), [showTypedToast]);

    // ======================================================================
    // Data loading
    // ======================================================================
    const loadData = useCallback(async (cId) => {
        setLoading(true);
        const guid = getCampusGuid(cId);

        try {
            const [config, devices, reasons, globalR, server, mysqlReasons] = await Promise.all([
                fetchCampusConfig(guid),
                fetchDeviceNames(guid),
                fetchReasonCodes(guid),
                fetchGlobalReasonCodes(guid),
                fetchServerConfig(cId),
                fetchMysqlReasonCodes(cId),
            ]);

            setConfigData(config || {});
            setDeviceData(devices || []);
            setReasonData(reasons?.active || []);
            setAllReasonData(reasons?.all || []);
            setMysqlReasonCount(mysqlReasons.length);
            setGlobalReasons(globalR || []);
            setServerData(Array.isArray(server) && server.length > 0 ? server[0] : (server || {}));
        } catch (err) {
            console.error('[SystemSettingsPage] Load error:', err);
            showToast('Failed to load settings data.', 'error');
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    // Initial load
    useEffect(() => {
        loadData(campusId);
    }, [campusId, loadData]);

    // ======================================================================
    // Campus switch handler
    // ======================================================================
    const handleCampusChange = (newId) => {
        const session = getSession();
        const oldIsCloud = session?.campusData?.[campusId?.toString()]?.is_cloud;
        const newIsCloud = session?.campusData?.[newId?.toString()]?.is_cloud;

        setCampusId(newId);
        updateSessionCampus(newId);

        // If switching between cloud and non-cloud, reload the whole page
        if (oldIsCloud !== newIsCloud) {
            window.location.reload();
        }
    };

    // ======================================================================
    // Save handlers
    // ======================================================================
    const handleSaveAppSettings = async (formData) => {
        setSaving(true);
        const guid = getCampusGuid(campusId);
        const timestamp = getUTCTimestamp();
        const payload = [{
            ...formData,
            CampusId: guid,
            ModifiedBy: getUsername(),
            DateModified: timestamp,
        }];

        const [ok] = await saveCampusConfig(payload, guid);
        setSaving(false);

        if (ok) {
            setConfigData((prev) => ({ ...prev, ...formData }));
            showToast('App settings saved.');
        } else {
            showToast('Failed to save app settings.', 'error');
        }
    };

    const handleSaveCustomSettings = async (formData) => {
        setSaving(true);
        const guid = getCampusGuid(campusId);
        const timestamp = getUTCTimestamp();
        const payload = [{
            ...formData,
            CampusId: guid,
            ModifiedBy: getUsername(),
            DateModified: timestamp,
        }];

        const [ok] = await saveCampusConfig(payload, guid);
        setSaving(false);

        if (ok) {
            setConfigData((prev) => ({ ...prev, ...formData }));
            showToast('Customization settings saved.');
        } else {
            showToast('Failed to save customization settings.', 'error');
        }
    };

    const handleSaveAlertSettings = async (formData) => {
        setSaving(true);
        const guid = getCampusGuid(campusId);
        const timestamp = getUTCTimestamp();
        const payload = [{
            ...formData,
            CampusId: guid,
            ModifiedBy: getUsername(),
            DateModified: timestamp,
        }];

        const [ok] = await saveCampusConfig(payload, guid);
        setSaving(false);

        if (ok) {
            setConfigData((prev) => ({ ...prev, ...formData }));
            showToast('Alert settings saved.');
        } else {
            showToast('Failed to save alert settings.', 'error');
        }
    };

    const handleSaveDevices = async (selectedDevices) => {
        setSaving(true);
        const guid = getCampusGuid(campusId);
        const timestamp = getUTCTimestamp();
        const payload = [{
            AFRDevices: selectedDevices,
            CampusId: guid,
            ModifiedBy: getUsername(),
            DateModified: timestamp,
        }];

        const [ok] = await saveCampusConfig(payload, guid);
        setSaving(false);

        if (ok) {
            setConfigData((prev) => ({ ...prev, AFRDevices: selectedDevices }));
            setDeviceDrawerOpen(false);
            showToast('Device settings saved.');
        } else {
            showToast('Failed to save device settings.', 'error');
        }
    };

    /* Re-read both stores after every reason save, success or not. The Lambda picks new ids and
       saves row by row, so a failed batch may still have saved some rows; the server's lists are
       the only truth (docs/REASON_CODE_FIX_PLAN.md B1). */
    const reloadReasons = async () => {
        const [reasons, mysqlReasons] = await Promise.all([
            fetchReasonCodes(getCampusGuid(campusId)),
            fetchMysqlReasonCodes(campusId),
        ]);
        setReasonData(reasons?.active || []);
        setAllReasonData(reasons?.all || []);
        setMysqlReasonCount(mysqlReasons.length);
    };

    const handleSaveReasonEdits = async (changedPayload) => {
        setSaving(true);
        const guid = getCampusGuid(campusId);
        const timestamp = getUTCTimestamp();

        const payload = changedPayload.map((item) => ({
            ...item,
            CampusId: guid,
            ModifiedBy: getUsername(),
            DateModified: timestamp,
        }));

        const [ok] = await editReasonCodes(payload, {
            campusId,
            userId: getUserId(),
            clientId: getSessionId(),
        });
        await reloadReasons();
        setSaving(false);

        if (ok) {
            showToast('Reason codes updated.');
        } else {
            showToast('Failed to update reason codes. The list shows what was saved.', 'error');
        }
    };

    const handleAddReason = async ({ description, category }) => {
        setSaving(true);
        const guid = getCampusGuid(campusId);
        const timestamp = getUTCTimestamp();

        // No ReasonCodeId: the Lambda assigns one that has never been used on this campus.
        const payload = [{
            Description: description,
            GlobalReasonCode: category,
            Deleted: false,
            CampusId: guid,
            ModifiedBy: getUsername(),
            DateModified: timestamp,
            DateCreated: timestamp,
        }];

        const [ok] = await addReasonCodes(payload, {
            campusId,
            userId: getUserId(),
            clientId: getSessionId(),
        });
        await reloadReasons();
        setSaving(false);

        if (ok) {
            setReasonDrawerOpen(false);
            showToast('Reason added.');
        } else {
            showToast('Failed to add reason.', 'error');
        }
    };

    const handleBulkCreate = async () => {
        setSaving(true);
        const guid = getCampusGuid(campusId);
        const timestamp = getUTCTimestamp();
        const base = { CampusId: guid, ModifiedBy: getUsername(), DateModified: timestamp, DateCreated: timestamp };

        // Ids are assigned by the Lambda in this order (1-8 on a campus that has never had any).
        const payload = [
            { Description: 'Medical Emergency', GlobalReasonCode: '18', ...base },
            { Description: 'Toileting', GlobalReasonCode: '2', ...base },
            { Description: 'Mobility', GlobalReasonCode: '8', ...base },
            { Description: 'Dressing/Grooming', GlobalReasonCode: '9', ...base },
            { Description: 'Housekeeping', GlobalReasonCode: '15', ...base },
            { Description: 'Medication', GlobalReasonCode: '5', ...base },
            { Description: 'Food/Drink', GlobalReasonCode: '7', ...base },
            { Description: 'Other', GlobalReasonCode: '1', ...base },
        ];

        const [ok] = await addReasonCodes(payload, {
            campusId,
            userId: getUserId(),
            clientId: getSessionId(),
        });
        await reloadReasons();
        setSaving(false);
        setBulkDialogOpen(false);

        if (ok) {
            showToast('Default reason codes created.');
        } else {
            showToast('Failed to create default reason codes. The list shows what was saved.', 'error');
        }
    };

    const handleDeleteReason = async () => {
        setSaving(true);
        const guid = getCampusGuid(campusId);
        const timestamp = getUTCTimestamp();

        const payload = [{
            Deleted: true,
            ReasonCodeId: deleteTarget.id,
            CampusId: guid,
            DateModified: timestamp,
            ModifiedBy: getUsername(),
            TTL: getTTL(7),
        }];

        const [ok] = await deleteReasonCode(payload, {
            campusId,
            userId: getUserId(),
            clientId: getSessionId(),
        });
        await reloadReasons();
        setSaving(false);
        setDeleteDialogOpen(false);

        if (ok) {
            showToast('Reason deleted.');
        } else {
            showToast('Failed to delete reason.', 'error');
        }
    };

    const handleSaveServerSettings = async (formData) => {
        setSaving(true);
        const payload = [formData];

        const [ok] = await saveServerSettings(payload, {
            campusId,
            userId: getUserId(),
            clientId: getSessionId(),
        });
        setSaving(false);

        if (ok) {
            setServerData((prev) => ({ ...prev, ...formData }));
            showToast('Server settings saved.');
        } else {
            showToast('Failed to save server settings.', 'error');
        }
    };

    // ======================================================================
    // Delete reason dialog trigger
    // ======================================================================
    const openDeleteDialog = (id, description) => {
        setDeleteTarget({ id, description });
        setDeleteDialogOpen(true);
    };

    // Next reason number for the add drawer
    const nextReasonNumber = reasonData.length + 1;

    // ======================================================================
    // Render
    // ======================================================================
    const renderTabContent = () => {
        if (loading) {
            return (
                <div className="pp-section">
                    <div className="pp-section__body">
                        <div className="ss-skeleton" style={{ width: '60%', height: '16px', marginBottom: 'var(--space-3)' }} />
                        <div className="ss-skeleton" style={{ width: '100%', height: '44px', marginBottom: 'var(--space-3)' }} />
                        <div className="ss-skeleton" style={{ width: '80%', height: '16px', marginBottom: 'var(--space-3)' }} />
                        <div className="ss-skeleton" style={{ width: '100%', height: '44px' }} />
                    </div>
                </div>
            );
        }

        return activeTab === 'app' ? (
            <AppSettingsTab
                alertTone={configData?.AlertTone || ''}
                campusTimeZone={configData?.CampusTimeZone ?? 0}
                logOutTimeInterval={configData?.LogOutTimeInterval || 1}
                onSave={handleSaveAppSettings}
                saving={saving}
            />
        ) : activeTab === 'custom' ? (
            <CustomizationTab
                userEditDisplayName={!!configData?.UserEditDisplayName}
                caregiverChgGroupEnabled={!!configData?.CaregiverChgGroupEnabled}
                caregiverChgToneEnabled={!!configData?.CaregiverChgToneEnabled}
                onSave={handleSaveCustomSettings}
                saving={saving}
            />
        ) : activeTab === 'alert' ? (
            <AlertSettingsTab
                acceptEnabled={!!configData?.AcceptButtonEnabled}
                finishEnabled={!!configData?.FinishButtonEnabled}
                reasonEnabled={!!configData?.ReasonCodeEnabled}
                releaseTimeout={configData?.ReleaseTimeInterval || 1}
                finishTimeout={configData?.FinishTimeInterval || 5}
                clearTimeout={configData?.ClearTimeInterval || 4}
                highPriorityTypes={configData?.HighPriorityAlertTypes || []}
                onSave={handleSaveAlertSettings}
                saving={saving}
            />
        ) : activeTab === 'device' ? (
            <DeviceSettingsTab
                afrDevices={configData?.AFRDevices}
                onEditDevices={() => setDeviceDrawerOpen(true)}
            />
        ) : activeTab === 'reason' ? (
            <ReasonSettingsTab
                reasonCodes={reasonData}
                globalReasons={globalReasons}
                onSaveEdits={handleSaveReasonEdits}
                onAddReason={() => setReasonDrawerOpen(true)}
                onBulkCreate={() => setBulkDialogOpen(true)}
                /* Only on a campus with no reason history in either store: defaults added on top of
                   deleted or report-only rows made duplicate sets (docs/REASON_CODE_FIX_PLAN.md B2). */
                canCreateDefaults={allReasonData.length === 0 && mysqlReasonCount === 0}
                onDeleteReason={openDeleteDialog}
                saving={saving}
            />
        ) : activeTab === 'server' ? (
            <ServerSettingsTab
                emergencyContact={serverData?.emergency_contact || ''}
                onSave={handleSaveServerSettings}
                saving={saving}
            />
        ) : null;
    };

    return (
        <>
            <NavigationV2 />
            <div className="nav2-main">
                <div className="ss-page">
                    {/* Toast notification */}
                    <Toast toast={toast} onDismiss={dismissToast} />

                    {/* Page header */}
                    <div className="ps-page-header">
                        <div className="ps-page-header__inner">
                            <div className="ps-breadcrumb-row">
                                <nav className="ps-breadcrumb">
                                    <span className="ps-breadcrumb__text">Settings</span>
                                    <span className="ps-breadcrumb__separator">/</span>
                                    <span className="ps-breadcrumb__current">System Settings</span>
                                </nav>
                                <div className="ps-campus-row">
                                    <label htmlFor="ss-campus-selector" className="ps-campus-label">Campus:</label>
                                    {campusList.length > 1 ? (
                                        <select
                                            id="ss-campus-selector"
                                            value={campusId}
                                            onChange={(e) => handleCampusChange(parseInt(e.target.value, 10))}
                                            className="ps-campus-select"
                                        >
                                            {campusList.map((c) => (
                                                <option key={c.campus_id} value={c.campus_id}>
                                                    {c.campus_name}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        <span className="ps-campus-name">{campusName}</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tab bar */}
                    <div className="pp-tab-bar">
                        {TABS.map((tab) => (
                            <button
                                key={tab.key}
                                className={`pp-tab${activeTab === tab.key ? ' is-active' : ''}`}
                                onClick={() => setActiveTab(tab.key)}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* Scrollable tab content */}
                    <div className="pp-tab-content">
                        {renderTabContent()}
                    </div>

                    {/* Drawers & Dialogs */}
                    <DeviceDrawer
                        open={deviceDrawerOpen}
                        onClose={() => setDeviceDrawerOpen(false)}
                        onSave={handleSaveDevices}
                        saving={saving}
                        currentDevices={configData?.AFRDevices || []}
                        allDevices={deviceData}
                    />

                    <ReasonDrawer
                        open={reasonDrawerOpen}
                        onClose={() => setReasonDrawerOpen(false)}
                        onSave={handleAddReason}
                        saving={saving}
                        globalReasons={globalReasons}
                        nextReasonNumber={nextReasonNumber}
                    />

                    <DeleteReasonDialog
                        open={deleteDialogOpen}
                        onClose={() => setDeleteDialogOpen(false)}
                        onConfirm={handleDeleteReason}
                        saving={saving}
                        reasonDescription={deleteTarget.description}
                    />

                    {/* Bulk Create Defaults dialog */}
                    {bulkDialogOpen ? (
                        <div className="pp-dialog-backdrop" onClick={() => setBulkDialogOpen(false)}>
                            <div className="pp-dialog" onClick={(e) => e.stopPropagation()}>
                                <div className="pp-dialog__header">
                                    <h3 className="pp-dialog__title">Create Default Reason Codes</h3>
                                    <button className="dp-drawer__close" onClick={() => setBulkDialogOpen(false)}>
                                        <i className="fe fe-x" />
                                    </button>
                                </div>
                                <div className="pp-dialog__body">
                                    <p className="pp-dialog__text" style={{ marginBottom: 'var(--space-3)' }}>
                                        Do you wish to add the following default reasons?
                                    </p>
                                    <ul style={{ margin: 0, paddingLeft: 'var(--space-5)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)', lineHeight: 1.8 }}>
                                        <li>Medical Emergency</li>
                                        <li>Toileting</li>
                                        <li>Mobility</li>
                                        <li>Dressing/Grooming</li>
                                        <li>House Keeping</li>
                                        <li>Medication</li>
                                        <li>Food/Drink</li>
                                        <li>Other</li>
                                    </ul>
                                </div>
                                <div className="pp-dialog__footer">
                                    <button className="dp-btn dp-btn--cancel" onClick={() => setBulkDialogOpen(false)}>
                                        No
                                    </button>
                                    <button className="dp-btn dp-btn--primary" disabled={saving} onClick={handleBulkCreate}>
                                        {saving ? 'Creating...' : 'Yes'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
        </>
    );
};

export default SystemSettingsPage;
